// src/products/analysis/pages/CryptoPage.js
import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, TrendingUp, TrendingDown, RefreshCw, X } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { getTopCryptocurrencies, getCryptoCandles } from '../services/cryptoService';

const CryptoPage = () => {
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [chartRange, setChartRange] = useState(30);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  useEffect(() => {
    loadCryptoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCryptoData = async () => {
    setLoading(true);
    setError(null);

    try {
      const cryptoData = await getTopCryptocurrencies(25);
      
      if (!cryptoData || cryptoData.length === 0) {
        setError('No cryptocurrency data available. Please try again later.');
        setCryptos([]);
      } else {
        setCryptos(cryptoData);
      }
    } catch (err) {
      let errorMessage = err.message || 'Failed to load cryptocurrency data. Please try again.';
      
      // Handle rate limit errors specifically
      if (errorMessage.includes('Rate limit') || errorMessage.includes('429')) {
        errorMessage = 'CoinGecko API rate limit exceeded. Please wait 60 seconds and refresh. Free tier allows 50 calls/minute.';
      }
      
      setError(errorMessage);
      console.error('Crypto loading error:', err);
      setCryptos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    
    // Format with appropriate decimal places based on price
    if (price >= 1000) {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (price >= 1) {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    } else {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 })}`;
    }
  };

  const formatChange = (change, changePercent) => {
    if (changePercent === null || changePercent === undefined) {
      if (change === null || change === undefined) return 'N/A';
      return `${change >= 0 ? '+' : ''}${formatPrice(change)}`;
    }
    
    const isPositive = changePercent >= 0;
    const sign = isPositive ? '+' : '';
    return `${sign}${changePercent.toFixed(2)}%`;
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap === null || marketCap === undefined) return 'N/A';
    
    if (marketCap >= 1e12) {
      return `$${(marketCap / 1e12).toFixed(2)}T`;
    } else if (marketCap >= 1e9) {
      return `$${(marketCap / 1e9).toFixed(2)}B`;
    } else if (marketCap >= 1e6) {
      return `$${(marketCap / 1e6).toFixed(2)}M`;
    } else {
      return `$${marketCap.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  };

  const fetchCryptoHistory = async (coinId, days) => {
    if (!coinId) return;

    setChartLoading(true);
    setChartError(null);

    try {
      const candles = await getCryptoCandles(coinId, days);

      const formatted = candles
        .map((candle) => ({
          timestamp: candle.timestamp,
          close: candle.close
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      setChartData(formatted);
    } catch (err) {
      setChartData([]);
      setChartError(err.message || 'Failed to load historical data.');
    } finally {
      setChartLoading(false);
    }
  };

  const handleCryptoSelect = async (crypto) => {
    if (!crypto || crypto.id === undefined) {
      return;
    }

    setSelectedCrypto(crypto);
    setChartRange(30);
    await fetchCryptoHistory(crypto.id, 30);
  };

  const handleRangeChange = async (rangeValue) => {
    if (!selectedCrypto || rangeValue === chartRange) {
      return;
    }

    setChartRange(rangeValue);
    await fetchCryptoHistory(selectedCrypto.id, rangeValue);
  };

  const closeModal = () => {
    setSelectedCrypto(null);
    setChartData([]);
    setChartError(null);
    setChartRange(30);
  };

  const rangeOptions = [
    { label: '30D', value: 30 },
    { label: '90D', value: 90 },
    { label: '6M', value: 180 },
    { label: '1Y', value: 365 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cryptocurrency Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Top 25 cryptocurrencies by market cap with quick history
          </p>
        </div>
        <button
          onClick={loadCryptoData}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && cryptos.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading cryptocurrency data...</span>
        </div>
      )}

      {/* Crypto Cards Grid */}
      {!loading && cryptos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cryptos.map((crypto) => {
            // CoinGecko provides 7-day change directly, fallback to 24h if not available
            const sevenDayChange = crypto.sevenDayChange !== null && crypto.sevenDayChange !== undefined
              ? crypto.sevenDayChange
              : crypto.priceChangePercent;
            const isPositive = sevenDayChange !== null && sevenDayChange >= 0;

            return (
              <div
                key={crypto.id || crypto.symbol}
                role="button"
                tabIndex={0}
                onClick={() => handleCryptoSelect(crypto)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCryptoSelect(crypto);
                  }
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {crypto.image && (
                      <img
                        src={crypto.image}
                        alt={crypto.name}
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{crypto.name}</h3>
                      <p className="text-sm text-gray-500">{crypto.displaySymbol}</p>
                    </div>
                  </div>
                  {crypto.marketCapRank && (
                    <span className="text-xs text-gray-400">#{crypto.marketCapRank}</span>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Current Price */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(crypto.currentPrice)}
                    </p>
                  </div>

                  {/* Market Cap */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Market Cap</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatMarketCap(crypto.marketCap)}
                    </p>
                  </div>

                  {/* 7-Day Change */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      {crypto.sevenDayChange !== null && crypto.sevenDayChange !== undefined 
                        ? '7-Day Change' 
                        : '24h Change'}
                    </p>
                    <div className="flex items-center gap-2">
                      {sevenDayChange !== null && sevenDayChange !== undefined ? (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-5 h-5 text-green-500" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-500" />
                          )}
                          <p
                            className={`text-lg font-semibold ${
                              isPositive ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatChange(null, sevenDayChange)}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400">N/A</p>
                      )}
                    </div>
                  </div>

                  {/* 24h High/Low */}
                  {(crypto.high24h || crypto.low24h) && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-gray-500">24h High</p>
                          <p className="font-medium text-gray-900">
                            {formatPrice(crypto.high24h)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">24h Low</p>
                          <p className="font-medium text-gray-900">
                            {formatPrice(crypto.low24h)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && cryptos.length === 0 && !error && (
        <div className="text-center py-12">
          <p className="text-gray-500">No cryptocurrency data available.</p>
        </div>
      )}

      {selectedCrypto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedCrypto.name} Price History
                </h2>
                <p className="text-sm text-gray-500">
                  USD quotes sourced from CoinGecko
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600"
                aria-label="Close chart modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="flex flex-wrap gap-2">
                {rangeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleRangeChange(option.value)}
                    disabled={chartLoading && chartRange === option.value}
                    className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                      chartRange === option.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-200 text-gray-600 hover:text-blue-600 hover:border-blue-400'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="h-80">
                {chartLoading ? (
                  <div className="flex h-full items-center justify-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading historical prices…</span>
                  </div>
                ) : chartError ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-red-500">{chartError}</p>
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="cryptoPriceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(value) =>
                          new Date(value).toLocaleDateString(
                            'en-US',
                            chartRange <= 180
                              ? { month: 'short', day: 'numeric' }
                              : { month: 'short', year: '2-digit' }
                          )
                        }
                        stroke="#9ca3af"
                      />
                      <YAxis
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(value) =>
                          `$${value.toLocaleString('en-US', {
                            minimumFractionDigits: value >= 10 ? 2 : 4,
                            maximumFractionDigits: value >= 10 ? 2 : 6
                          })}`
                        }
                        stroke="#9ca3af"
                      />
                      <Tooltip
                        formatter={(value) => [
                          `$${Number(value).toLocaleString('en-US', {
                            minimumFractionDigits: value >= 10 ? 2 : 4,
                            maximumFractionDigits: value >= 10 ? 2 : 6
                          })}`,
                          'Close'
                        ]}
                        labelFormatter={(label) =>
                          new Date(label).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour12: false
                          })
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="close"
                        stroke="#2563eb"
                        strokeWidth={2}
                        fill="url(#cryptoPriceGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-gray-500">No historical data available.</p>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500">
                Historical data defaults to the last 30 days. Select another range to explore up to 12 months of
                pricing.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoPage;

